'use client';

import Link from 'next/link';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { SettingsSuspenseFallback } from '@/platform/layout';
import { useRouteId } from '@/platform/lib/useRouteId';
import { cartItemCount, getSellerCart, type SellerCart } from '@/platform/commerce/api';
import { getMarketplaceVendor } from '@/platform/marketplace/api';
import { SellerCartPanel } from '@/platform/marketplace/SellerCartPanel';
import { getPublicProfile } from '@/platform/users/api';
import { getVendorSettings } from '@/platform/vendor/api';
import {
  StorefrontPagesList,
  VendorLinkedClaimsFeed,
  VendorProductsList,
  VendorStoreHeader,
} from '@/platform/vendor/VendorProfileSection';
import { MarketplacePageFrame } from '../MarketplacePageFrame';

type Props = {
  sellerId: string;
};

export function MarketplaceStorefrontPage(props: Props) {
  return (
    <Suspense fallback={<SettingsSuspenseFallback title="Seller store" />}>
      <MarketplaceStorefrontContent {...props} />
    </Suspense>
  );
}

function MarketplaceStorefrontContent({ sellerId: paramId }: Props) {
  const sellerId = useRouteId('sellers', paramId) || paramId;
  const searchParams = useSearchParams();
  const router = useRouter();
  const focusProductId = searchParams.get('product')?.trim() || undefined;
  const cartOpen = searchParams.get('cart') === '1';
  const [verified, setVerified] = useState(false);
  const [storeName, setStoreName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [openCart, setOpenCart] = useState<SellerCart | null>(null);
  const [cartLoading, setCartLoading] = useState(true);

  const refreshCart = useCallback(() => {
    if (!sellerId) return Promise.resolve();
    setCartLoading(true);
    return getSellerCart(sellerId)
      .then((r) => setOpenCart(r.cart))
      .catch(() => setOpenCart(null))
      .finally(() => setCartLoading(false));
  }, [sellerId]);

  useEffect(() => {
    if (!sellerId) return;
    Promise.allSettled([
      getMarketplaceVendor(sellerId),
      getPublicProfile(sellerId),
      getVendorSettings(sellerId),
    ])
      .then(([listing, profile, settings]) => {
        if (listing.status === 'fulfilled') {
          setVerified(true);
          setStoreName(listing.value.storeName || listing.value.displayName || null);
          return;
        }
        if (profile.status === 'fulfilled') {
          setVerified(!!profile.value.vendor);
          setStoreName(profile.value.displayName?.trim() || profile.value.handle || null);
          return;
        }
        if (
          settings.status === 'fulfilled' &&
          settings.value.storePublic &&
          settings.value.connectOnboardingComplete
        ) {
          setVerified(true);
          setStoreName(settings.value.storeName?.trim() || null);
          return;
        }
        setVerified(false);
        setStoreName(null);
      })
      .finally(() => setLoading(false));
  }, [sellerId]);

  useEffect(() => {
    void refreshCart();
  }, [refreshCart]);

  function openCartPanel() {
    const params = new URLSearchParams(searchParams.toString());
    params.set('cart', '1');
    router.push(`/marketplace/sellers/${sellerId}/?${params.toString()}`);
  }

  function closeCartPanel() {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('cart');
    const qs = params.toString();
    router.push(`/marketplace/sellers/${sellerId}/${qs ? `?${qs}` : ''}`);
  }

  const cartCount = cartItemCount(openCart);
  const frameActions =
    verified && cartCount > 0 ? (
      <button type="button" className="btn btn-secondary" onClick={openCartPanel}>
        View cart ({cartCount})
      </button>
    ) : undefined;

  if (!sellerId) {
    return (
      <MarketplacePageFrame title="Store not found" description="Invalid seller link.">
        <p className="text-muted">This seller link is not valid.</p>
      </MarketplacePageFrame>
    );
  }

  return (
    <MarketplacePageFrame
      title={storeName ? `${storeName}'s store` : 'Seller store'}
      description={
        loading
          ? 'Loading…'
          : verified
            ? 'Add items to your cart and check out with Stripe.'
            : 'This seller is not verified for hosted checkout.'
      }
      actions={frameActions}
    >
      <div className="feed-card settings-page marketplace-storefront">
        {cartOpen && (
          <SellerCartPanel
            sellerUserId={sellerId}
            storeName={storeName ?? undefined}
            cart={openCart}
            onCartChange={(cart) => {
              setOpenCart(cart);
              void refreshCart();
            }}
            onClose={closeCartPanel}
          />
        )}
        <VendorStoreHeader vendorUserId={sellerId} />
        <VendorProductsList
          vendorUserId={sellerId}
          isVerifiedSeller={verified}
          focusProductId={focusProductId}
          openCart={openCart}
          cartLoading={cartLoading}
          onCartChange={setOpenCart}
          onOpenCart={openCartPanel}
        />
        <StorefrontPagesList vendorUserId={sellerId} />
        <VendorLinkedClaimsFeed vendorUserId={sellerId} />
        <p className="text-muted marketplace-storefront__profile-link">
          <Link href={`/users/${sellerId}/`}>View full member profile</Link>
        </p>
      </div>
    </MarketplacePageFrame>
  );
}
